import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-big-calendar';
import useSWR from 'swr';
import { useClient } from '../../../../hooks/useClient';
import { useResourcePanel } from '../../../../hooks/useResourcePanel';
import { Reservation } from '../../../../types/reservation';
import { ProfileOrganization } from '../types';
import { getReservationsDateRange } from '../utils';

export const useReservationsCalendar = ({
  organization,
  externalUserId,
}: {
  organization: ProfileOrganization;
  externalUserId: string | undefined;
}) => {
  const { get } = useClient();

  // State for controlled calendar
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [calendarView, setCalendarView] = useState<View>('month');

  // Calculate date range based on current date and view
  const calendarDateRange = useMemo(() => {
    return getReservationsDateRange(calendarDate, calendarView);
  }, [calendarDate, calendarView]);

  const reservationsUrl = useMemo(() => {
    if (!organization || !externalUserId) return null;
    const startDate = calendarDateRange.start.toISOString();
    const endDate = calendarDateRange.end.toISOString();
    return `/organizations/${organization.id}/reservations?externalUserId=${externalUserId}&startDate=${startDate}&endDate=${endDate}`;
  }, [organization, externalUserId, calendarDateRange]);

  const {
    data: reservations,
    mutate: mutateReservations,
    isLoading: reservationsLoading,
  } = useSWR(reservationsUrl, get<Reservation[]>);
  const {
    isEditing: editingReservation,
    setIsEditing: setEditingReservation,
    panelVisible: reservationPanelVisible,
    closePanel: closeReservationPanel,
    selectedResource: selectedReservation,
    openPanel: openReservationPanel,
  } = useResourcePanel(reservations);

  const handleCalendarNavigate = useCallback((newDate: Date) => {
    setCalendarDate(newDate);
  }, []);

  const handleCalendarViewChange = useCallback((view: View) => {
    setCalendarView(view);
  }, []);

  return {
    calendarDate,
    calendarView,
    reservations,
    mutateReservations,
    reservationsLoading,
    editingReservation,
    setEditingReservation,
    reservationPanelVisible,
    closeReservationPanel,
    selectedReservation,
    openReservationPanel,
    handleCalendarNavigate,
    handleCalendarViewChange,
  };
};
